import axios from 'axios';
import {
  CUSTOM_DOMAINS,
  DEV_HOST,
  EXPERIMENT_URL,
  PREVIEW_CHANNEL,
  SSO_PREFIX,
  EXPERIMENT_URL_2,
  EXPERIMENT_URL_3,
  DEV_MACHINE,
} from 'constants/host';

import {
  clearLocalStorage,
  getStringFromLocalStorage,
  removeFromLocalStorage,
  setStringToLocalStorage,
} from './localStorage';

import Firebase from 'config/Firebase';
import { NotificationInput, NotificationType } from 'interfaces/notifications';
import jwt_decode from 'jwt-decode';
import { Client } from 'types/enums';
import {
  NOTIFICATIONS_API_KEY,
  NOTIFICATIONS_ENDPOINT,
  SEND_NOTIFICATIONS,
  SUBDOMAIN_SSO,
} from 'config/variables';
import {
  PERMISSIONS,
  PermissionGroupAtom,
} from 'components/pages/Admin/Permissions/types/permissions';
import { SettingsBoss } from 'interfaces/client';
import { CompanyModules } from 'interfaces/amplitud_user';
import { generateCustomToken } from 'components/pages/SSOAuth/API/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import firebase from 'firebase/app';

export const getClientId = (defaultClientId: string): string => {
  const url = window.location.host;
  const urlForLookup = url.startsWith('www.') ? url.slice(4) : url;

  // Get subdomain if any (usar host normalizado para no obtener "www" como subdomain)
  let subdomain = urlForLookup.split('.')[0];

  // Return default client id if on localhost or preview channel (link desechable)
  if (
    subdomain.includes(DEV_HOST) ||
    url.includes(PREVIEW_CHANNEL) ||
    url.includes(EXPERIMENT_URL) ||
    url.includes(EXPERIMENT_URL_2) ||
    url.includes(EXPERIMENT_URL_3) ||
    subdomain.includes(DEV_MACHINE)
  ) {
    return defaultClientId;
    // or return subdomain if it is a custom domain in the list
  } else if (Object.values(Client).includes(subdomain as Client)) {
    return subdomain;
  }

  // Set subdomain based on custom domain (match con y sin www.)
  for (const [domain, clientId] of Object.entries(CUSTOM_DOMAINS)) {
    if (urlForLookup === domain) {
      return clientId;
    }
  }

  // Set client by name of temporal link
  const subdomainRegex = new RegExp(
    'lapzo-platform-stg--(\\w+)-.+(\\.firebaseapp\\.com|\\.web\\.app)$',
  );
  const matches = url.match(subdomainRegex);

  if (matches) return matches[1];

  // Return subdomain if no custom domain is found
  return subdomain;
};

export const removeToken = (shouldRedirect = false, redirectErrorUrl = '') => {
  Firebase.auth
    .signOut()
    .then(() => {
      removeFromLocalStorage('user');
      removeFromLocalStorage('email');
      removeFromLocalStorage('token');
      removeFromLocalStorage('userFb');
      removeFromLocalStorage('id');
      clearLocalStorage();
      if (shouldRedirect) {
        window.location.replace(redirectErrorUrl || '/signin');
      }
    })
    .catch(error => { });
};

const isCognitoToken = (token: string): boolean => {
  try {
    const decoded: any = jwt_decode(token);
    return decoded.iss?.includes('cognito-idp');
  } catch {
    return false;
  }
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: any = jwt_decode(token);
    return Date.now() > decoded.exp * 1000;
  } catch {
    return true;
  }
};

const waitForFirebaseUser = (): Promise<firebase.User | null> =>
  new Promise(resolve => {
    if (Firebase.auth.currentUser) {
      resolve(Firebase.auth.currentUser);
      return;
    }

    let settled = false;
    const settle = (user: firebase.User | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      resolve(user);
    };

    const timeout = setTimeout(
      () => settle(Firebase.auth.currentUser),
      5000,
    );

    const unsubscribe = Firebase.auth.onAuthStateChanged(user => {
      if (user) settle(user);
    });
  });

export const getBootstrapToken = async (): Promise<string | undefined> => {
  const storedToken = getStringFromLocalStorage('token');
  if (!storedToken) return undefined;

  if (isCognitoToken(storedToken)) {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const newToken = session.tokens?.idToken?.toString();
      if (newToken) {
        setStringToLocalStorage('token', newToken);
        return newToken;
      }
    } catch {
      if (!isTokenExpired(storedToken)) return storedToken;
    }
    return undefined;
  }

  if (!isTokenExpired(storedToken)) {
    return storedToken;
  }

  const user = await waitForFirebaseUser();
  if (user) {
    const token = await user.getIdToken(true);
    setStringToLocalStorage('token', token);
    return token;
  }

  return undefined;
};

export const decodeTokenValidate = async (): Promise<string | undefined> => {
  let currentToken = getStringFromLocalStorage('token');

  try {
    if (currentToken) {
      const decodedToken: any = jwt_decode(currentToken);
      const expirationDate = decodedToken.exp * 1000;
      const curDate = Date.now();
      const { iss } = decodedToken;

      if (iss?.includes('cognito-idp')) {

        if (curDate > expirationDate) {
          const tokenResponse = await generateCustomToken(
            decodedToken['cognito:username'],
          );
          const refreshedToken =
            typeof tokenResponse === 'object'
              ? tokenResponse?.tokenId
              : tokenResponse;

          if (refreshedToken) {
            setStringToLocalStorage('token', refreshedToken);
            currentToken = refreshedToken;
          }
        }
        return currentToken;
      }
      else {
        if (curDate > expirationDate) {
          const { currentUser } = Firebase.auth;
          if (currentUser) {
            const tokenResult = await currentUser?.getIdToken(true);
            if (tokenResult) {
              currentToken = tokenResult;
              setStringToLocalStorage('token', currentToken);
            } else {
              removeToken(true);
            }
          } else {
            removeToken();
            return undefined;
          }
        }
        return currentToken;
      }
    } else {
      //no existe el token
      removeToken();
    }
  } catch (error) {
    console.log(`AngieLog ~ decodeTokenValidate ~ error:`, error);
    removeToken();
  }
};

export const numberWithCommas = (num: number | string): string => {
  const stringNumber = typeof num === 'number' ? num.toString() : num;
  return stringNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const encode = (str: string): string => {
  return window.btoa(str);
};

export const decode = (ciphertext: string): string => {
  return window.atob(ciphertext);
};

export const getLXPUrl = (subdomain: string, path = '', website: string) => {
  // This url will change based on environment // lernitlms for dev, lapzo for prod

  const setSubdomain = SUBDOMAIN_SSO;
  const isProd = SUBDOMAIN_SSO.includes('lapzo');
  return website && isProd
    ? `${website}${path}`
    : `https://${subdomain}.${setSubdomain}${path}`;
};

export const getLXPUrlWithSSO = (
  subdomain: string,
  path: string,
  userId: string,
  website: string,
) => {
  const cipherUserUserId = encode(userId);

  return getLXPUrl(
    subdomain,
    `${SSO_PREFIX}${path}--${cipherUserUserId}`,
    website,
  );
};

export const goToLXPUrlWithSSO = (
  subdomain: string,
  path: string,
  userId: string,
  website: string,
): void => {
  // Same tab
  window.location.href = getLXPUrlWithSSO(subdomain, path, userId, website);
  // New tab
  // window.open(`${baseUrl}${SSO_PREFIX}${path}--${cipherUserUserId}`, '_blank');
};

export const goToPerformanceWithSSO = (subdomain: string, userId: string) => {
  const token = encode(userId);
  const URL = `https://${subdomain}-performance.lernit.app/sso/auth/${token}`;
  // window.location.href = URL;
  window.open(URL, '_self');
};
export const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const capitalizeFormat = (text: string) => {
  let newText = '';
  // Trim the text
  const trimText = text.trim();

  // Get every word of the text
  const everyWord = trimText.split(' ');

  // Capitalize every word and make the new string
  for (let index = 0; index < everyWord.length; index++) {
    if (everyWord[index] !== '') {
      if (index !== 0) {
        newText = `${newText} ${everyWord[index]
          .charAt(0)
          .toUpperCase()}${everyWord[index].slice(1).toLowerCase()}`;
      } else {
        newText =
          newText +
          everyWord[index].charAt(0).toUpperCase() +
          everyWord[index].slice(1).toLowerCase();
      }
    }
  }

  return newText;
};

export const sendNotification = async ({
  template,
  email,
  variables,
  copyToEmails,
}: NotificationInput): Promise<void> => {
  try {
    if (!SEND_NOTIFICATIONS) {
      return;
    }

    if (!NOTIFICATIONS_ENDPOINT || NOTIFICATIONS_ENDPOINT === '') {
      throw new Error('The NOTIFICATIONS_ENDPOINT env dont exist.');
    }

    if (!NOTIFICATIONS_API_KEY || NOTIFICATIONS_API_KEY === '') {
      throw new Error('The NOTIFICATIONS_API_KEY env dont exist.');
    }

    if (!template) {
      throw new Error('Template was not passed down');
    }
    if (!variables.user_name) {
      throw new Error('Variable user_name is required');
    }
    const payload = {
      type: NotificationType.Email,
      email,
      template_id: template,
      variables,
      copyToEmails,
    };

    await axios.post(`${NOTIFICATIONS_ENDPOINT}/notifications`, payload, {
      headers: {
        'x-api-key': NOTIFICATIONS_API_KEY,
      },
    });
  } catch (error) { }
};

export function getEnumValueByKey<T>(
  enumObject: T,
  key: string,
): T[keyof T] | undefined {
  const upperKey = key.toUpperCase() as keyof T;
  return enumObject[upperKey];
}

export const checkPermission = (
  permission: PermissionGroupAtom,
  keys: PERMISSIONS[],
) => {
  return keys.some(key => permission[key] === true);
};

export const getQuery = <T extends Record<string, unknown | undefined>>(
  params: T,
): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(item => {
    const [key, value] = item;
    if (value === undefined) return;
    const formattedValue = typeof value !== 'string' ? String(value) : value;
    searchParams.append(key, formattedValue);
  });
  return searchParams.toString();
};

export const replaceBossLabel = ({
  settings,
  text,
  translate,
  isPlural = false,
}: {
  text: string;
  translate: string;
  settings: SettingsBoss;
  isPlural?: boolean;
}): string => {
  const bossLabel = settings?.bossLabel || translate;
  const bossPluralLabel = settings?.bossPluralLabel || translate;
  const replacement = isPlural ? bossPluralLabel : bossLabel;
  return text.replaceAll('{boss}', replacement);
};

export const checkCompanyModules = (
  clientModules: { [key in CompanyModules]?: boolean } | null | undefined,
): CompanyModules[] => {
  return Object.entries(clientModules ?? {})
    .filter(([_, value]) => value && typeof value === 'boolean')
    .map(([key]) => CompanyModules[key as keyof typeof CompanyModules]);
};
